import { AnimatePresence, motion } from "framer-motion";
import { I18n } from "@assets/strings";
import React, { PropsWithChildren, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useLockBodyScroll } from "@utils/hooks";
import { Z } from "@ui/styles/zIndex";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { HookWrapper } from "./hookWrapper";

interface ClosableBottomSheetProps extends PropsWithChildren {
	isOpen: boolean;
	fitContent?: boolean;
	maxHeight?: number;
	closeOnSelect?: boolean;
	onClose: () => void;
}

export const ClosableBottomSheet: React.FC<ClosableBottomSheetProps> = ({
	isOpen,
	maxHeight = 500,
	onClose,
	fitContent = false,
	closeOnSelect,
	children,
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const [componentHeight, setComponentHeight] = useState<number>();
	const height = componentHeight && componentHeight > maxHeight ? maxHeight : componentHeight;

	useLayoutEffect(() => {
		if (ref.current) {
			setComponentHeight(ref.current.getBoundingClientRect().height);
		}
	}, [ref.current, isOpen]);

	return (
		<AnimatePresence>
			{isOpen && (
				<HookWrapper hook={() => useLockBodyScroll()}>
					{() => (
						<>
							<BackgroundMask
								onClick={onClose}
								initial={{ opacity: 0 }}
								animate={{ opacity: 0.7 }}
								exit={{ opacity: 0 }}
								transition={{ ease: "easeInOut", duration: 0.3 }}
							/>
							<BottomSheetContainer
								ref={ref}
								$hasOffsetY={!!height && height < maxHeight}
								$fitContent={fitContent}
								height={height === maxHeight ? height : undefined}
								initial={{ bottom: `-${height ?? maxHeight + 15}px` }}
								animate={{ bottom: "-15px" }}
								exit={{ bottom: `-${height ?? maxHeight + 15}px` }}
								transition={{ ease: "easeInOut", duration: 0.3 }}
							>
								<Content onClick={() => closeOnSelect && onClose()}>{children}</Content>
								<Cancel>
									<PrimaryButton onClick={onClose}>{I18n.t("cancel")}</PrimaryButton>
								</Cancel>
							</BottomSheetContainer>
						</>
					)}
				</HookWrapper>
			)}
		</AnimatePresence>
	);
};

const BottomSheetContainer = styled(motion.div)<{ height?: number; $hasOffsetY: boolean; $fitContent?: boolean }>`
	background-color: ${({ theme }) => theme.colors.white};
	height: ${({ height, $fitContent }) => ($fitContent ? (height ? `${height}px` : "fit-content") : "fit-content")};
	display: flex;
	flex: 1;
	width: 100%;
	position: fixed;
	left: 0;
	right: 0;
	border-top-left-radius: 10px;
	border-top-right-radius: 10px;
	padding-top: 20px;
	padding-left: 20px;
	padding-right: 20px;
	padding-bottom: ${({ $hasOffsetY }) => ($hasOffsetY ? 100 : 40)}px;
	z-index: ${Z.toast};
	overflow-x: hidden;
`;

const Content = styled.div`
	width: 100%;
`;

const BackgroundMask = styled(motion.div)`
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: ${({ theme }) => theme.colors.black};
	z-index: ${Z.above(Z.modal)};
	overflow-x: hidden;
`;

const Cancel = styled.div`
	position: absolute;
	bottom: 15px;
	right: 0;
	left: 0;
	padding: 16px 20px;
	background-color: ${({ theme }) => theme.colors.white};
	> button {
		width: 100%;
	}
`;
